import { toast } from "components/ui/use-toast";

import {
  GamificationSettingKey,
  type IGamificationSetting,
} from "@/@types/gamification";
import {
  useGetGamificationSettingsQuery,
  useUpdateGamificationSettingMutation,
} from "@/services/gamification";

import { GAMIFICATION_SETTINGS_COPY } from "../constants/messages";
import { getApiErrorMessage } from "../utils/api-error";

export interface UseGamificationSettingsResult {
  locationRadiusKm: number | undefined;
  isLoading: boolean;
  isError: boolean;
  isSaving: boolean;
  retry: () => void;
  saveLocationRadius: (radiusKm: number) => Promise<boolean>;
}

function radiusOf(settings: IGamificationSetting[]): number | undefined {
  const setting: IGamificationSetting | undefined = settings.find(
    (item: IGamificationSetting): boolean =>
      item.key === GamificationSettingKey.CAMPAIGN_LOCATION_RADIUS_KM,
  );
  const radius: number = Number(setting?.value);
  return Number.isInteger(radius) ? radius : undefined;
}

export function useGamificationSettings(): UseGamificationSettingsResult {
  const { data, isFetching, isError, refetch } = useGetGamificationSettingsQuery(
    undefined,
    { refetchOnMountOrArgChange: true },
  );
  const [updateSetting, { isLoading: isSaving }] =
    useUpdateGamificationSettingMutation();

  const saveLocationRadius = async (radiusKm: number): Promise<boolean> => {
    try {
      await updateSetting({
        key: GamificationSettingKey.CAMPAIGN_LOCATION_RADIUS_KM,
        value: String(radiusKm),
      }).unwrap();
      toast({
        title: GAMIFICATION_SETTINGS_COPY.savedTitle,
        description: GAMIFICATION_SETTINGS_COPY.savedDescription(radiusKm),
      });
      return true;
    } catch (error) {
      toast({
        title: GAMIFICATION_SETTINGS_COPY.saveErrorTitle,
        description: getApiErrorMessage(
          error,
          GAMIFICATION_SETTINGS_COPY.saveError,
        ),
        variant: "destructive",
      });
      return false;
    }
  };

  return {
    locationRadiusKm: radiusOf(data ?? []),
    isLoading: isFetching,
    isError,
    isSaving,
    retry: refetch,
    saveLocationRadius,
  };
}
